import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import BottomTabNavigation from '../../components/navigation/BottomTabNavigation';
import Icon from '../../components/AppIcon';
import AnalyticsMetricCard from './components/AnalyticsMetricCard';
import EngagementChart from './components/EngagementChart';
import SymptomTrendChart from './components/SymptomTrendChart';
import ComparisonChart from './components/ComparisonChart';
import FilterControls from './components/FilterControls';

import HeatMapView from './components/HeatMapView';
import { useAuth } from '../../contexts/AuthContext';
import { hasFeatureAccess, OFFICER_FEATURES } from '../../utils/rolePermissions';

import caregiverService from '../../services/caregiverService';
import { logger } from '../../utils/logger';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';




const CaregiverAnalyticsDashboard = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [managedProfiles, setManagedProfiles] = useState([]);
  const [error, setError] = useState('');
  
  // Filter states
  const [selectedDependent, setSelectedDependent] = useState('all');
  const [dateRange, setDateRange] = useState({
    start: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd')
  });
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    loadAnalyticsData();
  }, [selectedDependent, dateRange, selectedCategory]);

  // Check officer access
  const hasAccess = hasFeatureAccess(userProfile, OFFICER_FEATURES?.CAREGIVER_ANALYTICS);

  // Redirect non-officers
  useEffect(() => {
    if (!loading && userProfile && !hasAccess) {
      navigate('/settings-and-privacy');
    }
  }, [hasAccess, loading, userProfile, navigate]);

  const loadAnalyticsData = async () => {
    setLoading(true);
    setError('');

    try {
      // Load managed profiles
      const { data: profiles, error: profilesError } = await caregiverService?.getManagedProfiles();
      if (profilesError) {
        setError(profilesError?.message || 'Failed to load managed profiles');
        logger?.error('Failed to load managed profiles', profilesError);
        setLoading(false);
        return;
      }
      setManagedProfiles(profiles || []);

      // Load analytics data
      const { data: analytics, error: analyticsError } = await caregiverService?.getAnalytics({
        managedProfileId: selectedDependent !== 'all' ? selectedDependent : null,
        dateRange: {
          start: startOfDay(new Date(dateRange?.start))?.toISOString(),
          end: endOfDay(new Date(dateRange?.end))?.toISOString()
        },
        category: selectedCategory !== 'all' ? selectedCategory : null
      });

      if (analyticsError) {
        setError(analyticsError?.message || 'Failed to load analytics data');
        logger?.error('Failed to load analytics', analyticsError);
      } else {
        setAnalyticsData(analytics);
      }
    } catch (err) {
      setError('An unexpected error occurred');
      logger?.error('Analytics load error', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportReport = async () => {
    try {
      const { data, error } = await caregiverService?.exportAnalyticsReport({
        managedProfileId: selectedDependent !== 'all' ? selectedDependent : null,
        dateRange,
        category: selectedCategory !== 'all' ? selectedCategory : null
      });

      if (error) {
        logger?.error('Failed to export report', error);
        return;
      }

      // Create download link
      const blob = new Blob([data], { type: 'text/csv' });
      const url = window?.URL?.createObjectURL(blob);
      const link = document?.createElement('a');
      link.href = url;
      link.download = `caregiver-analytics-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document?.body?.appendChild(link);
      link?.click();
      document?.body?.removeChild(link);
      window?.URL?.revokeObjectURL(url);

      logger?.info('Analytics report exported');
    } catch (err) {
      logger?.error('Export error', err);
    }
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Analytics Dashboard - MyBodyToday</title>
          <meta name="description" content="Comprehensive analytics for caregiver engagement and dependent user health tracking" />
        </Helmet>
        <div className="min-h-screen bg-background flex items-center justify-center pb-20">
          <div className="flex flex-col items-center gap-4">
            <Icon name="Loader2" size={40} className="text-primary animate-spin" strokeWidth={2} />
            <p className="text-muted-foreground">Loading analytics...</p>
          </div>
        </div>
        <BottomTabNavigation />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Analytics Dashboard - MyBodyToday</title>
        <meta name="description" content="Comprehensive analytics for caregiver engagement and dependent user health tracking" />
      </Helmet>

      <div className="min-h-screen bg-background pb-20">
        {/* Header */}
        <div className="bg-card border-b border-border sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-2.5 py-2 sm:px-3 sm:py-2.5 md:px-4 md:py-3">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-base sm:text-lg md:text-xl font-heading font-bold text-foreground">
                  Analytics Dashboard
                </h1>
                <p className="text-xs sm:text-sm md:text-base text-muted-foreground mt-0.5">
                  Comprehensive insights into dependent user engagement and health patterns
                </p>
              </div>
              <button
                onClick={handleExportReport}
                className="flex items-center gap-1.5 px-2.5 py-1.5 sm:px-3 sm:py-2 md:px-4 md:py-2 bg-primary text-primary-foreground rounded-lg text-xs sm:text-sm font-medium transition-smooth hover:bg-primary/90"
              >
                <Icon name="Download" size={14} className="sm:w-4 sm:h-4 md:w-[18px] md:h-[18px]" />
                <span className="hidden sm:inline">Export</span>
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24">
          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-error/10 border border-error rounded-lg text-error text-sm">
              {error}
            </div>
          )}

          {/* Filter Controls */}
          <FilterControls
            managedProfiles={managedProfiles}
            selectedDependent={selectedDependent}
            onDependentChange={setSelectedDependent}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
          />

          {/* Key Metrics */}
          {analyticsData && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <AnalyticsMetricCard
                  icon="Activity"
                  label="Check-in Completion Rate"
                  value={`${analyticsData?.checkInCompletionRate || 0}%`}
                  trend={analyticsData?.checkInTrend}
                  color="primary"
                />
                <AnalyticsMetricCard
                  icon="Users"
                  label="Caregiver Engagement Rate"
                  value={`${analyticsData?.caregiverEngagementRate || 0}%`}
                  trend={analyticsData?.engagementTrend}
                  color="success"
                />
                <AnalyticsMetricCard
                  icon="TrendingUp"
                  label="Symptom Logging Frequency"
                  value={analyticsData?.symptomLoggingFrequency || 0}
                  trend={analyticsData?.loggingTrend}
                  color="warning"
                />
                <AnalyticsMetricCard
                  icon="Target"
                  label="Feature Adoption Score"
                  value={`${analyticsData?.featureAdoptionScore || 0}%`}
                  trend={analyticsData?.adoptionTrend}
                  color="error"
                />
              </div>

              {/* Charts Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Engagement Chart */}
                <div className="bg-card rounded-xl border border-border shadow-therapeutic p-6">
                  <h2 className="text-lg font-heading font-semibold text-foreground mb-4">
                    Engagement Patterns Over Time
                  </h2>
                  <EngagementChart data={analyticsData?.engagementData || []} />
                </div>

                {/* Symptom Trend Chart */}
                <div className="bg-card rounded-xl border border-border shadow-therapeutic p-6">
                  <h2 className="text-lg font-heading font-semibold text-foreground mb-4">
                    Symptom Severity Trends
                  </h2>
                  <SymptomTrendChart data={analyticsData?.symptomTrendData || []} />
                </div>
              </div>

              {/* Comparison Chart */}
              {managedProfiles?.length > 1 && selectedDependent === 'all' && (
                <div className="bg-card rounded-xl border border-border shadow-therapeutic p-6 mb-8">
                  <h2 className="text-lg font-heading font-semibold text-foreground mb-4">
                    Multi-Dependent Comparison
                  </h2>
                  <ComparisonChart 
                    data={analyticsData?.comparisonData || []} 
                    profiles={managedProfiles}
                  />
                </div>
              )}

              {/* Heat Map */}
              <div className="bg-card rounded-xl border border-border shadow-therapeutic p-6">
                <h2 className="text-lg font-heading font-semibold text-foreground mb-4">
                  Optimal Check-in Timing Heat Map
                </h2>
                <HeatMapView data={analyticsData?.heatMapData || []} />
              </div>

              {/* Insights Section */}
              {analyticsData?.insights && analyticsData?.insights?.length > 0 && (
                <div className="mt-8 bg-card rounded-xl border border-border shadow-therapeutic p-6">
                  <h2 className="text-lg font-heading font-semibold text-foreground mb-4">
                    Predictive Insights & Recommendations
                  </h2>
                  <div className="space-y-3">
                    {analyticsData?.insights?.map((insight, index) => (
                      <div 
                        key={index}
                        className="flex items-start gap-3 p-4 bg-primary/5 rounded-lg border border-primary/20"
                      >
                        <Icon 
                          name={insight?.type === 'warning' ? 'AlertTriangle' : 'Lightbulb'} 
                          size={20} 
                          className={insight?.type === 'warning' ? 'text-warning' : 'text-primary'}
                          strokeWidth={2}
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-foreground mb-1">
                            {insight?.title}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {insight?.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Empty State */}
          {!analyticsData && !loading && (
            <div className="bg-card rounded-xl border border-border shadow-therapeutic p-12 text-center">
              <Icon name="BarChart3" size={48} className="text-muted-foreground mx-auto mb-4" strokeWidth={2} />
              <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                No Analytics Data Available
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                Start tracking symptoms and check-ins to see comprehensive analytics
              </p>
              <button
                onClick={() => navigate('/caregiver-dashboard')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium transition-smooth hover:bg-primary/90"
              >
                <Icon name="ArrowLeft" size={18} strokeWidth={2} />
                Back to Dashboard
              </button>
            </div>
          )}
        </div>
      </div>

      <BottomTabNavigation />
    </>
  );
};

export default CaregiverAnalyticsDashboard;